import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './RifleScore.css';
import { ArrowLeft, Zap } from 'lucide-react';
import { rifleService } from '../../../../../services';

const RifleZeroDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [zero, setZero] = useState(null);
    const [form, setForm] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activityMessage, setActivityMessage] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchZero = async () => {
            try {
                const zeroData = await rifleService.getZero(id);
                setZero(zeroData);
                setForm({
                    equipmentId: zeroData.equipmentId ?? '',
                    rifleName: zeroData.rifleName || '',
                    scopeName: zeroData.scopeName || '',
                    distance: zeroData.distance ?? '',
                    distanceUnit: zeroData.distanceUnit || 'metres',
                    clicksElevation: zeroData.clicksElevation ?? '',
                    clicksWindage: zeroData.clicksWindage ?? '',
                    elevationDirection: zeroData.elevationDirection || '',
                    windageDirection: zeroData.windageDirection || '',
                    clickValue: zeroData.clickValue ?? '',
                    clickUnit: zeroData.clickUnit || '',
                    ammunition: zeroData.ammunition || '',
                    muzzleVelocity: zeroData.muzzleVelocity ?? '',
                    ballisticCoefficient: zeroData.ballisticCoefficient ?? '',
                    ballisticNotes: zeroData.ballisticNotes || '',
                    zeroConfirmed: Boolean(zeroData.zeroConfirmed),
                    confirmationShots: zeroData.confirmationShots ?? '',
                    groupSize: zeroData.groupSize ?? '',
                    groupSizeUnit: zeroData.groupSizeUnit || 'mm',
                    verticalOffset: zeroData.verticalOffset ?? '',
                    horizontalOffset: zeroData.horizontalOffset ?? '',
                    offsetUnit: zeroData.offsetUnit || 'mm',
                    confirmationNotes: zeroData.confirmationNotes || '',
                    zeroDate: zeroData.zeroDate ? new Date(zeroData.zeroDate).toISOString().slice(0, 10) : '',
                    conditions: zeroData.conditions || '',
                    notes: zeroData.notes || '',
                    isBaseZero: Boolean(zeroData.isBaseZero)
                });
            } catch (err) {
                console.error('Error loading zero record:', err);
                setError('Unable to load this zero record.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchZero();
    }, [id]);

    const handleSave = async () => {
        if (!form) {
            return;
        }

        try {
            setIsSaving(true);
            setError(null);
            await rifleService.updateZero(id, {
                equipmentId: form.equipmentId === '' ? null : Number(form.equipmentId),
                rifleName: form.rifleName || null,
                scopeName: form.scopeName || null,
                distance: form.distance === '' ? 0 : Number(form.distance),
                distanceUnit: form.distanceUnit || null,
                clicksElevation: form.clicksElevation === '' ? null : Number(form.clicksElevation),
                clicksWindage: form.clicksWindage === '' ? null : Number(form.clicksWindage),
                elevationDirection: form.elevationDirection || null,
                windageDirection: form.windageDirection || null,
                clickValue: form.clickValue === '' ? null : Number(form.clickValue),
                clickUnit: form.clickUnit || null,
                ammunition: form.ammunition || null,
                muzzleVelocity: form.muzzleVelocity === '' ? null : Number(form.muzzleVelocity),
                ballisticCoefficient: form.ballisticCoefficient === '' ? null : Number(form.ballisticCoefficient),
                ballisticNotes: form.ballisticNotes || null,
                zeroConfirmed: form.zeroConfirmed,
                confirmationShots: form.confirmationShots === '' ? null : Number(form.confirmationShots),
                groupSize: form.groupSize === '' ? null : Number(form.groupSize),
                groupSizeUnit: form.groupSizeUnit || null,
                verticalOffset: form.verticalOffset === '' ? null : Number(form.verticalOffset),
                horizontalOffset: form.horizontalOffset === '' ? null : Number(form.horizontalOffset),
                offsetUnit: form.offsetUnit || null,
                confirmationNotes: form.confirmationNotes || null,
                zeroDate: form.zeroDate ? new Date(form.zeroDate).toISOString() : new Date().toISOString(),
                conditions: form.conditions || null,
                notes: form.notes || null,
                isBaseZero: form.isBaseZero
            });

            setZero((current) => ({
                ...current,
                equipmentId: form.equipmentId === '' ? null : Number(form.equipmentId),
                rifleName: form.rifleName || null,
                scopeName: form.scopeName || null,
                distance: form.distance === '' ? 0 : Number(form.distance),
                distanceUnit: form.distanceUnit || null,
                clicksElevation: form.clicksElevation === '' ? null : Number(form.clicksElevation),
                clicksWindage: form.clicksWindage === '' ? null : Number(form.clicksWindage),
                elevationDirection: form.elevationDirection || null,
                windageDirection: form.windageDirection || null,
                clickValue: form.clickValue === '' ? null : Number(form.clickValue),
                clickUnit: form.clickUnit || null,
                ammunition: form.ammunition || null,
                muzzleVelocity: form.muzzleVelocity === '' ? null : Number(form.muzzleVelocity),
                ballisticCoefficient: form.ballisticCoefficient === '' ? null : Number(form.ballisticCoefficient),
                ballisticNotes: form.ballisticNotes || null,
                zeroConfirmed: form.zeroConfirmed,
                confirmationShots: form.confirmationShots === '' ? null : Number(form.confirmationShots),
                groupSize: form.groupSize === '' ? null : Number(form.groupSize),
                groupSizeUnit: form.groupSizeUnit || null,
                verticalOffset: form.verticalOffset === '' ? null : Number(form.verticalOffset),
                horizontalOffset: form.horizontalOffset === '' ? null : Number(form.horizontalOffset),
                offsetUnit: form.offsetUnit || null,
                confirmationNotes: form.confirmationNotes || null,
                zeroDate: form.zeroDate ? new Date(form.zeroDate).toISOString() : null,
                conditions: form.conditions || null,
                notes: form.notes || null,
                isBaseZero: form.isBaseZero
            }));
            setActivityMessage('Zero updated');
            setTimeout(() => setActivityMessage(null), 2500);
        } catch (saveError) {
            console.error('Error updating zero record:', saveError);
            setError('Could not update this zero record.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="rifle-loading">
                <div className="rifle-spinner"></div>
                <p>Loading zero record...</p>
            </div>
        );
    }

    if (error || !zero) {
        return <div className="rifle-error-message">{error || 'Zero record not found.'}</div>;
    }

    return (
        <div className="rifle-score-page">
            <div className="rifle-detail-actions">
                <button className="rifle-secondary-btn" onClick={() => navigate('/dashboard/score/rifle')}>
                    <ArrowLeft size={16} />
                    Back to rifle dashboard
                </button>
            </div>

            <div className="rifle-header">
                <div className="rifle-header-content">
                    <div className="rifle-header-icon">
                        <Zap size={28} />
                    </div>
                    <div className="rifle-header-text">
                        <h1>{zero.equipmentName || zero.rifleName || 'Rifle zero'}</h1>
                        <p>{zero.distance} {zero.distanceUnit || 'metres'} zero</p>
                    </div>
                </div>
            </div>

            {error ? <div className="rifle-error-message">{error}</div> : null}
            {activityMessage ? <div className="rifle-success-message">{activityMessage}</div> : null}

            <section className="rifle-detail-card">
                <h3>Edit zero</h3>
                <div className="rifle-form-grid">
                    <input
                        type="text"
                        placeholder="Rifle name"
                        value={form?.rifleName || ''}
                        onChange={(event) => setForm((current) => ({ ...current, rifleName: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Scope name"
                        value={form?.scopeName || ''}
                        onChange={(event) => setForm((current) => ({ ...current, scopeName: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Distance"
                        value={form?.distance ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, distance: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Distance unit"
                        value={form?.distanceUnit || ''}
                        onChange={(event) => setForm((current) => ({ ...current, distanceUnit: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.1"
                        placeholder="Clicks elevation"
                        value={form?.clicksElevation ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, clicksElevation: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Elevation direction"
                        value={form?.elevationDirection || ''}
                        onChange={(event) => setForm((current) => ({ ...current, elevationDirection: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.1"
                        placeholder="Clicks windage"
                        value={form?.clicksWindage ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, clicksWindage: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Windage direction"
                        value={form?.windageDirection || ''}
                        onChange={(event) => setForm((current) => ({ ...current, windageDirection: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Click value"
                        value={form?.clickValue ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, clickValue: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Click unit"
                        value={form?.clickUnit || ''}
                        onChange={(event) => setForm((current) => ({ ...current, clickUnit: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Ammunition"
                        value={form?.ammunition || ''}
                        onChange={(event) => setForm((current) => ({ ...current, ammunition: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Muzzle velocity"
                        value={form?.muzzleVelocity ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, muzzleVelocity: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.001"
                        placeholder="Ballistic coefficient"
                        value={form?.ballisticCoefficient ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, ballisticCoefficient: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Confirmation shots"
                        value={form?.confirmationShots ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, confirmationShots: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Group size"
                        value={form?.groupSize ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, groupSize: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Group unit"
                        value={form?.groupSizeUnit || ''}
                        onChange={(event) => setForm((current) => ({ ...current, groupSizeUnit: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Vertical offset"
                        value={form?.verticalOffset ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, verticalOffset: event.target.value }))}
                    />
                    <input
                        type="number"
                        step="0.01"
                        placeholder="Horizontal offset"
                        value={form?.horizontalOffset ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, horizontalOffset: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Offset unit"
                        value={form?.offsetUnit || ''}
                        onChange={(event) => setForm((current) => ({ ...current, offsetUnit: event.target.value }))}
                    />
                    <input
                        type="date"
                        value={form?.zeroDate || ''}
                        onChange={(event) => setForm((current) => ({ ...current, zeroDate: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Conditions</label>
                    <input
                        type="text"
                        value={form?.conditions || ''}
                        onChange={(event) => setForm((current) => ({ ...current, conditions: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Ballistic notes</label>
                    <textarea
                        value={form?.ballisticNotes || ''}
                        onChange={(event) => setForm((current) => ({ ...current, ballisticNotes: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Zero confirmation notes</label>
                    <textarea
                        value={form?.confirmationNotes || ''}
                        onChange={(event) => setForm((current) => ({ ...current, confirmationNotes: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Notes</label>
                    <textarea
                        value={form?.notes || ''}
                        onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-actions">
                    <label className="rifle-checkbox-row">
                        <input
                            type="checkbox"
                            checked={Boolean(form?.zeroConfirmed)}
                            onChange={(event) => setForm((current) => ({ ...current, zeroConfirmed: event.target.checked }))}
                        />
                        Zero confirmed by group
                    </label>
                    <label className="rifle-checkbox-row">
                        <input
                            type="checkbox"
                            checked={Boolean(form?.isBaseZero)}
                            onChange={(event) => setForm((current) => ({ ...current, isBaseZero: event.target.checked }))}
                        />
                        Base zero
                    </label>
                    <button className="rifle-primary-btn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save zero'}
                    </button>
                </div>
            </section>

            <section className="rifle-detail-card">
                <h3>Zero details</h3>
                <div className="rifle-detail-list">
                    <p><strong>Rifle:</strong> {zero.rifleName || 'Not set'}</p>
                    <p><strong>Scope:</strong> {zero.scopeName || 'Not set'}</p>
                    <p><strong>Elevation:</strong> {zero.clicksElevation ?? 0} {zero.elevationDirection || ''}</p>
                    <p><strong>Windage:</strong> {zero.clicksWindage ?? 0} {zero.windageDirection || ''}</p>
                    <p><strong>Click value:</strong> {zero.clickValue ? `${zero.clickValue} ${zero.clickUnit || ''}` : 'Not set'}</p>
                    <p><strong>Ammunition:</strong> {zero.ammunition || 'Not set'}</p>
                    <p><strong>Muzzle velocity:</strong> {zero.muzzleVelocity || 'Not set'}</p>
                    <p><strong>Ballistic coefficient:</strong> {zero.ballisticCoefficient || 'Not set'}</p>
                    <p><strong>Zero confirmed:</strong> {zero.zeroConfirmed ? 'Yes' : 'No'}</p>
                    <p><strong>Confirmation group:</strong> {zero.groupSize ? `${zero.groupSize} ${zero.groupSizeUnit || ''}` : 'Not recorded'}</p>
                    <p><strong>Confirmation shots:</strong> {zero.confirmationShots || 'Not recorded'}</p>
                    <p><strong>Impact offset:</strong> {(zero.verticalOffset || zero.horizontalOffset) ? `${zero.verticalOffset ?? 0} vertical / ${zero.horizontalOffset ?? 0} horizontal ${zero.offsetUnit || ''}` : 'Centered / not recorded'}</p>
                    <p><strong>Conditions:</strong> {zero.conditions || 'Not recorded'}</p>
                    <p><strong>Zero date:</strong> {zero.zeroDate ? new Date(zero.zeroDate).toLocaleDateString() : 'Not set'}</p>
                    <p><strong>Base zero:</strong> {zero.isBaseZero ? 'Yes' : 'No'}</p>
                    <p><strong>Confirmation notes:</strong> {zero.confirmationNotes || 'None'}</p>
                    <p><strong>Notes:</strong> {zero.notes || 'None'}</p>
                </div>
            </section>
        </div>
    );
};

export default RifleZeroDetail;
